import { can, clientName } from '@clim/client-core';
import { isLateIntervention, isoDate, planningRange, PLANNING_VIEW_LABELS, weekStart, type InterventionRecord, type PlanningSummaryRow, type PlanningView } from '@clim/domain';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fmtDay, InterventionCard, InterventionDialog } from '../components/Interventions';
import { PartnerJobDialog } from '../components/PartnerJobDialog';
import { Empty, Input, Select } from '../components/ui';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

const VIEWS: PlanningView[] = ['day', 'week', 'month'];

/**
 * Planning (CDC §8) : vues Aujourd'hui / 7 jours / Mois, filtres territoire, technicien et commune ;
 * dossiers « À planifier », interventions à attribuer ou à réattribuer ; nacelles par commune / semaine.
 * Sans droit de planification : vue anonymisée (communes, volumes, nacelle).
 */
export function PlanningPage() {
  const { me } = useReady();
  return can(me, 'planning.manage') ? <PlannerView /> : <SummaryView />;
}

/** Technicien : ses interventions dont la date est passée sans compte rendu, signalées dès l'ouverture du planning. */
function MyLateBanner() {
  const engine = useEngine();
  const { me } = useReady();
  const today = isoDate(new Date());
  const late = engine.list('intervention').filter((i) => i.technicianId === me.user.id && isLateIntervention(i, today));
  if (!late.length) return null;
  return (
    <div className="error-box" role="alert">
      ⚠️ <strong>Vous avez {late.length} intervention(s) en retard</strong> (date passée, photos et compte rendu non faits).{' '}
      <Link to="/interventions">Les clôturer →</Link>
    </div>
  );
}

function Toolbar({ view, setView, day, setDay }: { view: PlanningView; setView(v: PlanningView): void; day: string; setDay(d: string): void }) {
  return (
    <div className="planning-bar">
      <div className="chips" role="tablist" aria-label="Vue">
        {VIEWS.map((v) => (
          <button key={v} type="button" role="tab" aria-selected={view === v} className={`chip${view === v ? ' on' : ''}`} onClick={() => setView(v)}>
            {PLANNING_VIEW_LABELS[v]}
          </button>
        ))}
      </div>
      <Input type="date" aria-label="À partir du" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} />
    </div>
  );
}

function PlannerView() {
  const engine = useEngine();
  const { me } = useReady();
  const online = useOnline();
  // sous-traitance : intervention pour un partenaire saisie en une fois (sans dossier de vente)
  const [partnerJob, setPartnerJob] = useState(false);
  const { territoryId } = useTerritory();
  const [view, setView] = useState<PlanningView>('week');
  const [day, setDay] = useState(() => isoDate(new Date()));
  const [tech, setTech] = useState('');
  const [city, setCity] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const range = planningRange(view, day);

  const all = engine.list('intervention').filter((i) => (!territoryId || i.territoryId === territoryId) && i.status !== 'cancelled');
  const technicians = [...new Map(all.filter((i) => i.technicianId).map((i) => [i.technicianId!, i.technicianName ?? '—'])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const cities = [...new Set(all.map((i) => i.city).filter(Boolean) as string[])].sort();
  const match = (i: InterventionRecord) => (!tech || i.technicianId === tech) && (!city || i.city === city);

  const toAssign = all.filter((i) => ['to_assign', 'impossible'].includes(i.status) && match(i));
  const waiting = all.filter((i) => (i.status === 'proposed' || (i.status === 'accepted' && !i.scheduledDate)) && match(i));
  const today = isoDate(new Date());
  const late = all.filter((i) => isLateIntervention(i, today) && match(i)).sort((a, b) => (a.scheduledDate ?? '').localeCompare(b.scheduledDate ?? ''));
  const dossiersToPlan = engine
    .list('dossier')
    .filter((d) => (!territoryId || d.territoryId === territoryId) && d.status === 'to_schedule' && !all.some((i) => i.dossierId === d.id && i.status !== 'impossible'));
  // §12 : SAV validés sans intervention en cours
  const savToPlan = engine
    .list('sav')
    .filter((s) => (!territoryId || s.territoryId === territoryId) && s.status === 'open');
  const planSav = (dossierId: string, savId: string) => void engine.createIntervention(dossierId, { savId }).then(setOpen, () => undefined);
  const byDay = new Map<string, InterventionRecord[]>();
  for (const i of all) {
    if (!i.scheduledDate || i.scheduledDate < range.from || i.scheduledDate > range.to || !match(i) || i.status === 'to_assign') continue;
    byDay.set(i.scheduledDate, [...(byDay.get(i.scheduledDate) ?? []), i]);
  }
  for (const list of byDay.values()) list.sort((a, b) => (a.slotStart ?? '99').localeCompare(b.slotStart ?? '99'));
  // nacelle : repérage par commune et par semaine
  const nacelles = new Map<string, Set<string>>();
  for (const i of all) {
    if (!i.needsNacelle || !i.scheduledDate || i.scheduledDate < range.from || i.scheduledDate > range.to) continue;
    const k = weekStart(i.scheduledDate);
    nacelles.set(k, new Set([...(nacelles.get(k) ?? []), i.city ?? '—']));
  }

  return (
    <div>
      <div className="page-head">
        <h1>Planning</h1>
        {can(me, 'dossier.schedule') && (
          <button className="btn primary" disabled={!online} onClick={() => setPartnerJob(true)}>
            + Intervention partenaire
          </button>
        )}
      </div>
      {partnerJob && <PartnerJobDialog onClose={() => setPartnerJob(false)} />}
      <Toolbar view={view} setView={setView} day={day} setDay={setDay} />
      <div className="filters">
        <Select aria-label="Technicien" value={tech} onChange={(e) => setTech(e.target.value)}>
          <option value="">Tous les techniciens</option>
          {technicians.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
        <Select aria-label="Commune" value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="">Toutes les communes</option>
          {cities.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </Select>
      </div>

      <div className="planning-grid">
        <div>
          {savToPlan.length > 0 && (
            <div className="card">
              <h2>SAV à planifier ({savToPlan.length})</h2>
              <ul className="list">
                {savToPlan.map((s) => {
                  const d = engine.get('dossier', s.dossierId);
                  const c = d ? engine.get('client', d.clientId) : undefined;
                  return (
                    <li key={s.id} className="row">
                      <span className="row-main">
                        <strong>
                          {c ? clientName(c) : 'Client'} · {engine.get('reason', s.reasonId)?.label ?? 'SAV'}
                        </strong>
                        <span className="muted">
                          {s.reference ?? d?.reference ?? '—'} · {d?.siteCity ?? c?.city ?? '—'}
                        </span>
                      </span>
                      <button className="btn small primary" onClick={() => planSav(s.dossierId, s.id)}>
                        Planifier
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {dossiersToPlan.length > 0 && (
            <div className="card">
              <h2>Dossiers à planifier ({dossiersToPlan.length})</h2>
              <ul className="list">
                {dossiersToPlan.map((d) => {
                  const c = engine.get('client', d.clientId);
                  return (
                    <li key={d.id}>
                      <Link to={`/dossiers/${d.id}`} className="row">
                        <span className="row-main">
                          <strong>{c ? clientName(c) : 'Client'}</strong>
                          <span className="muted">
                            {d.reference ?? 'Référence à la synchro'} · {d.siteCity ?? c?.city ?? '—'}
                          </span>
                        </span>
                        <span className="btn small">Planifier →</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {late.length > 0 && (
            <div className="card">
              <h2>⚠️ En retard ({late.length})</h2>
              <p className="muted">Date passée sans compte rendu du technicien (photos, « Terminée » ou « Impossible »).</p>
              {late.map((i) => (
                <InterventionCard key={i.id} i={i} onOpen={() => setOpen(i.id)}>
                  <div className="muted iv-date">
                    Prévue le {fmtDay(i.scheduledDate)} · {i.technicianName ?? 'sans technicien'}
                  </div>
                </InterventionCard>
              ))}
            </div>
          )}
          <div className="card">
            <h2>À attribuer / à réattribuer ({toAssign.length})</h2>
            {toAssign.length ? toAssign.map((i) => <InterventionCard key={i.id} i={i} onOpen={() => setOpen(i.id)} />) : <Empty>Rien à attribuer.</Empty>}
          </div>
          {waiting.length > 0 && (
            <div className="card">
              <h2>En attente ({waiting.length})</h2>
              <p className="muted">Proposées au technicien (en attente de sa réponse) ou acceptées sans date : fixez le jour.</p>
              {waiting.map((i) => (
                <InterventionCard key={i.id} i={i} onOpen={() => setOpen(i.id)} />
              ))}
            </div>
          )}
          {nacelles.size > 0 && (
            <div className="card">
              <h2>Nacelles prévues</h2>
              <ul className="plain">
                {[...nacelles.entries()].sort().map(([w, set]) => (
                  <li key={w}>
                    Semaine du {fmtDay(w)} : <strong>{[...set].sort().join(', ')}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div>
          {range.days.map((d) => {
            const list = byDay.get(d) ?? [];
            if (!list.length && view === 'month') return null;
            return (
              <section key={d} className="card day-card">
                <h2>
                  {fmtDay(d)} <span className="muted">· {list.length} intervention(s)</span>
                </h2>
                {list.length ? list.map((i) => <InterventionCard key={i.id} i={i} onOpen={() => setOpen(i.id)} />) : <Empty>Aucune intervention.</Empty>}
              </section>
            );
          })}
          {view === 'month' && !byDay.size && <Empty>Aucune intervention ce mois-ci.</Empty>}
        </div>
      </div>
      {open && <InterventionDialog id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Vue anonymisée (commerciaux) : où sont les équipes, combien d'interventions, nacelle prévue. */
function SummaryView() {
  const { api } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const [view, setView] = useState<PlanningView>('week');
  const [day, setDay] = useState(() => isoDate(new Date()));
  const [rows, setRows] = useState<PlanningSummaryRow[] | null>(null);
  const range = useMemo(() => planningRange(view, day), [view, day]);
  useEffect(() => {
    if (!online) return;
    const q = new URLSearchParams({ from: range.from, to: range.to, ...(territoryId ? { territoryId } : {}) });
    api.request<PlanningSummaryRow[]>('GET', `/v1/planning/summary?${q}`).then(setRows, () => setRows(null));
  }, [api, online, range.from, range.to, territoryId]);
  const byDay = new Map<string, PlanningSummaryRow[]>();
  for (const r of rows ?? []) byDay.set(r.date, [...(byDay.get(r.date) ?? []), r]);
  return (
    <div>
      <h1>Planning</h1>
      <MyLateBanner />
      <p className="muted">Vue d’ensemble anonymisée : communes et nombre d’interventions prévues.</p>
      <Toolbar view={view} setView={setView} day={day} setDay={setDay} />
      {!online && <Empty>Vue disponible en ligne.</Empty>}
      {online && rows && !rows.length && <Empty>Aucune intervention prévue sur la période.</Empty>}
      {[...byDay.entries()].map(([d, list]) => (
        <section key={d} className="card day-card">
          <h2>{fmtDay(d)}</h2>
          <ul className="plain">
            {list.map((r) => (
              <li key={r.city}>
                <strong>{r.city}</strong> · {r.count} intervention(s){r.nacelle ? ' · 🏗 nacelle' : ''}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
